import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import type { Group } from "@1js/pl-types";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import * as React from "react";
import { TriggerButton } from "../TriggerButton/TriggerButton";
import {
  RemoveFromWorkgroupCancelButtonLabel,
  RemoveFromWorkgroupMessage,
  RemoveFromWorkgroupRemoveButtonLabel,
  RemoveFromWorkgroupTitle,
  RemoveFromWorkgroupTitleWithPlaceholder,
} from "./RemoveFromWorkgroupDialog.strings";

export type RemoveFromWorkgroupDialogProps = {
  isOpen: boolean;
  onClose: (isClosedByCustomer: boolean) => void;
  onRemove: () => void;
  /**
   * The group from which the prompt is being removed.
   * If provided, the group name will be used in the dialog title.
   */
  group?: Group;
};

export const RemoveFromWorkgroupDialog: React.FC<
  RemoveFromWorkgroupDialogProps
> = ({ isOpen, onClose, onRemove, group }) => {
  const handleRemove = React.useCallback(() => {
    onRemove();
    onClose(false);
  }, [onClose, onRemove]);

  const handleClose = React.useCallback(() => {
    onClose(true);
  }, [onClose]);

  const localizations = useStrings({
    RemoveFromWorkgroupTitle,
    RemoveFromWorkgroupMessage,
    RemoveFromWorkgroupRemoveButtonLabel,
    RemoveFromWorkgroupCancelButtonLabel,
  });

  const localizationsWithPlaceholders = useStringsWithPlaceholders({
    RemoveFromWorkgroupTitleWithPlaceholder,
  });

  return (
    <Dialog open={isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {group?.DisplayName
              ? localizationsWithPlaceholders.RemoveFromWorkgroupTitleWithPlaceholder(
                  { groupName: group.DisplayName }
                )
              : localizations.RemoveFromWorkgroupTitle()}
          </DialogTitle>
          <DialogContent>
            {localizations.RemoveFromWorkgroupMessage()}
          </DialogContent>
          <DialogActions>
            <TriggerButton
              appearance="primary"
              onClick={handleRemove}
              disabled={!isOpen}
            >
              {localizations.RemoveFromWorkgroupRemoveButtonLabel()}
            </TriggerButton>
            <TriggerButton
              appearance="secondary"
              onClick={handleClose}
              disabled={!isOpen}
            >
              {localizations.RemoveFromWorkgroupCancelButtonLabel()}
            </TriggerButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
